import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import { useQuery, useMutation } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { Controller, useForm, FormProvider } from "react-hook-form";

import { TaskTrackerPropInterface } from "./TaskTracker";

import { getRunningTask , updateTask, createTask } from "@/api";
import { ProjectSelect } from "@/components/ProjectSelect";
import { StartTask } from "@/components/StartTask";
import { StopTask } from "@/components/StopTask";
import { TagSelect } from "@/components/TagSelect";
import { UpwardTimer } from "@/components/UpwardTimer";
import { useAppDispatch } from "@/redux";
import { updateOffsetTime } from "@/redux/reducers/taskRunner";
import { CreateTaskInterface } from "@/types/task";


export const TaskTracker: React.FC<TaskTrackerPropInterface> = ({ refetchTableData }) => {
  const dispatch = useAppDispatch();
  const formMethods = useForm<CreateTaskInterface>({
    mode: "onBlur",
  });

  const { handleSubmit, control, watch, getValues, reset } = formMethods;
  const description = watch("description");
  const project = watch("project");
  const tag = watch("tag");

  const {
    isLoading: isLoadingRunningTask,
    data: runningTask,
    refetch,
  } = useQuery({
    queryKey: ["get-running-task"],
    queryFn: async () => await getRunningTask(),
  });

  const { mutate: updateTaskMutation, isPending: updateTaskIsPending } =
    useMutation({
      mutationFn: async (data: CreateTaskInterface) => {
        if (runningTask?.data?.id) {
          return updateTask(runningTask?.data?.id, data);
        }
      },
      onSuccess: (response) => {
        refetch();
        refetchTableData();
        if (response?.data?.endTime) {
          reset();
          dispatch(updateOffsetTime({ runningTaskStartTime: '00:00:00' }));
        }
        if (response?.data?.startTime && !response?.data?.endTime) {
          dispatch(updateOffsetTime({ runningTaskStartTime: response?.data?.startTime }));
        }
      },
    });

  const { mutate: createTaskMutation, isPending: createTaskIsPending } =
    useMutation({
      mutationFn: async (data: CreateTaskInterface) => createTask(data),
      onSuccess: (response) => {
        refetch();
        refetchTableData();
        dispatch(updateOffsetTime({ runningTaskStartTime: response?.data?.startTime || '00:00:00' }));
      },
    });

  const submit = (data: CreateTaskInterface) => {
    runningTask?.data?.id ? updateTaskMutation(data) : createTaskMutation(data);
  };

  useEffect(() => {
    if (runningTask?.data?.id) {
      updateTaskMutation(getValues());
    }
  }, [getValues, runningTask?.data?.id, updateTaskMutation, description, project, tag]);

  useEffect(() => {
    if (runningTask?.data?.id) {
      reset(runningTask?.data);
      if (runningTask?.data?.startTime) {
        dispatch(updateOffsetTime({ runningTaskStartTime: runningTask?.data?.startTime }));
      }
    }
  }, [runningTask, reset]);

  const isLoading = updateTaskIsPending || createTaskIsPending || isLoadingRunningTask;

  return (
    <form onSubmit={handleSubmit(submit)}>
      <FormProvider {...formMethods}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          paddingLeft={2}
          paddingRight={2}
        >
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <InputBase
                {...field}
                fullWidth
                placeholder="What are you working on?"
                sx={{ fontSize: "2rem" }}
              />
            )}
          />
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <ProjectSelect
              project={project}
            />
            <TagSelect tag={tag} />
            <UpwardTimer
              startTime={runningTask?.data?.startTime}
            />
            {!runningTask?.data?.endTime && runningTask?.data?.id ? (
              <StopTask isLoading={isLoading} />
            ) : (
              <StartTask isLoading={isLoading} />
            )}
          </Stack>
        </Stack>
      </FormProvider>
    </form>
  );
};
